import axios from "../../api/axiosInstance";
import { STATUS, getProjectId, extractErrorInfo } from "./sharedfunction";

// Backticks in description bodies trip AWS WAF SQLi rule (libinjection tokenizes
// `/path` + "and"/"or" as a SQL boolean clause). Convert to single quotes pre-POST.
const sanitizeForWaf = (s) => (typeof s === "string" ? s.replace(/`/g, "'") : s);

export async function getTestCaseList({ 
  page = 1, 
  pageSize = 25, 
  selectedTestCaseIds,
  moduleId = "all",
  sortField,
  sortOrder,
  ast  // Required: AST must be provided (can be null for unfiltered)
} = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    // Normalize "default" to "all" for consistency (frontend UI uses "default" for "All Test Cases")
    const normalizedModuleId = moduleId === "default" ? "all" : moduleId;

    const body = {
      page,
      pageSize,
      ...(normalizedModuleId && { moduleId: normalizedModuleId }),
      ...(sortField && { sortField }),
      ...(sortOrder && { sortOrder }),
      ...(selectedTestCaseIds && Array.isArray(selectedTestCaseIds) && selectedTestCaseIds.length > 0 && { selectedTestCaseIds }),
      ast: ast ?? null  // AST is required (can be null for unfiltered queries) - explicitly set to null if undefined
    };

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/${projectId}/testcase/testcaselist`,
      body,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error fetching test case list:", error);
    return extractErrorInfo(error);
  }
}

export async function getAllTestCaseData({ 
  testCaseId,
  testCaseName,
  testCaseTitle,
  page = 1,
  limit = 15,
  onlyExecutionRecord,
  onlyInstructionAvailable,
} = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    const params = {
      ...(testCaseId && { testCaseId }),
      ...(testCaseName && { testCaseName }),
      ...(testCaseTitle && { testCaseTitle }),
      ...(page && { page }),
      ...(limit && { limit }),
      ...(onlyExecutionRecord && { onlyExecutionRecord }),
      ...(onlyInstructionAvailable && { onlyInstructionAvailable }),
    };

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/${projectId}/testcase/getAllTestCaseData`,
      {
        params,
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error fetching all test case data:", error);
    return extractErrorInfo(error);
  }
}

export async function getTestCaseDetail({ testCaseId, lastExecutionResultStatus } = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!testCaseId) {
      return {
        status: STATUS.ERROR,
        error: "Test Case ID is required",
      };
    }

    const params = {};
    if (lastExecutionResultStatus !== undefined) {
      params.lastExecutionResultStatus = lastExecutionResultStatus;
    }

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/${projectId}/testcase/${testCaseId}`,
      {
        params,
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error fetching test case detail:", error);
    return extractErrorInfo(error);
  }
}

export async function updateTestCase({ testCaseId, ...updateData } = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!testCaseId) {
      return {
        status: STATUS.ERROR,
        error: "Test Case ID is required",
      };
    }

    if (updateData.description !== undefined) {
      updateData.description = sanitizeForWaf(updateData.description);
    }

    const response = await axios.put(
      `${process.env.REACT_APP_API_URL}/${projectId}/testcase/${testCaseId}`,
      updateData,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error updating test case:", error);
    return extractErrorInfo(error);
  }
}

export async function updateTestCaseField({ testCaseId, field, value, updated_by } = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!testCaseId) {
      return {
        status: STATUS.ERROR,
        error: "Test Case ID is required",
      };
    }

    if (!field || value === undefined) {
      return {
        status: STATUS.ERROR,
        error: "Field and value are required",
      };
    }

    const payload = {
      field,
      value: field === "description" ? sanitizeForWaf(value) : value,
      ...(updated_by && { updated_by }),
    };

    const response = await axios.patch(
      `${process.env.REACT_APP_API_URL}/${projectId}/testcase/${testCaseId}/field`,
      payload,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error updating test case field:", error);
    return extractErrorInfo(error);
  }
}

export async function fetchModulesWithCount({ excludeTestGroupId, ast } = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    const params = {};
    if (excludeTestGroupId) {
      params.excludeTestGroupId = excludeTestGroupId;
    }

    // If AST is provided, use POST to send it in body, otherwise use GET
    const requestConfig = {
      params,
      withCredentials: true,
    };

    let response;
    // Use POST if AST is provided (even if it's an empty object, we want filtered counts)
    if (ast !== null && ast !== undefined) {
      // POST request with AST in body
      response = await axios.post(
        `${process.env.REACT_APP_API_URL}/${projectId}/testcase/fetchmodule`,
        { ast },
        requestConfig
      );
    } else {
      // GET request (backward compatible, no filters)
      response = await axios.get(
        `${process.env.REACT_APP_API_URL}/${projectId}/testcase/fetchmodule`,
        requestConfig
      );
    }

    
    // Backend returns: { success: true, data: { modulesData: [...] }, message: "..." }
    if (response?.data?.success) {
      // The data field contains { modulesData: [...] }
      const responseData = response?.data?.data;
      let modulesData = [];
      
      if (responseData) {
        // Check if it's already an array (direct modules array)
        if (Array.isArray(responseData)) {
          modulesData = responseData;
        } 
        // Check if it's an object with modulesData property
        else if (responseData.modulesData && Array.isArray(responseData.modulesData)) {
          modulesData = responseData.modulesData;
        } else {
          console.warn('Response data is not array and has no modulesData:', responseData);
        }
      } else {
        console.warn('No responseData found');
      }
      
      const result = {
        status: STATUS.SUCCESS,
        data: modulesData,
        message: response?.data?.message,
      };
      return result;
    }
    
    // Also handle direct modulesData in response (backward compatibility)
    if (response?.data?.modulesData && Array.isArray(response.data.modulesData)) {
      const result = {
        status: STATUS.SUCCESS,
        data: response.data.modulesData,
        message: response?.data?.message,
      };
      return result;
    }

    // If we get here, return empty array
    return {
      status: STATUS.SUCCESS,
      data: [],
      message: response?.data?.message,
    };

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error fetching modules with count:", error);
    return extractErrorInfo(error);
  }
}

export async function deleteTestCase({ ids } = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!ids || !Array.isArray(ids) || ids.length === 0) {
      return {
        status: STATUS.ERROR,
        error: "At least one test case ID is required",
      };
    }

    const response = await axios.delete(
      `${process.env.REACT_APP_API_URL}/${projectId}/testcase/delete`,
      {
        data: { ids },
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error deleting test case(s):", error);
    return extractErrorInfo(error);
  }
}

export async function cloneTestCase({ targetModuleId, testCaseId, testCaseIds } = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!targetModuleId) {
      return {
        status: STATUS.ERROR,
        error: "Target module ID is required",
      };
    }

    if (!testCaseId && (!testCaseIds || !Array.isArray(testCaseIds) || testCaseIds.length === 0)) {
      return {
        status: STATUS.ERROR,
        error: "Either testCaseId or testCaseIds array is required",
      };
    }

    const payload = {
      targetModuleId,
      ...(testCaseId && { testCaseId }),
      ...(testCaseIds && { testCaseIds }),
    };

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/${projectId}/testcase/clone`,
      payload,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error cloning test case(s):", error);
    return extractErrorInfo(error);
  }
}

export async function moveTestCase({ targetModuleId, testCaseId, testCaseIds } = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!targetModuleId) {
      return {
        status: STATUS.ERROR,
        error: "Target module ID is required",
      };
    }

    if (!testCaseId && (!testCaseIds || !Array.isArray(testCaseIds) || testCaseIds.length === 0)) {
      return {
        status: STATUS.ERROR,
        error: "Either testCaseId or testCaseIds array is required",
      };
    }

    const payload = {
      targetModuleId,
      ...(testCaseId && { testCaseId }),
      ...(testCaseIds && { testCaseIds }),
    };

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/${projectId}/testcase/move`,
      payload,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error moving test case(s):", error);
    return extractErrorInfo(error);
  }
}

export async function fetchExecutionHistory({ testCaseId, page = 1, pageSize = 25 } = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!testCaseId) {
      return {
        status: STATUS.ERROR,
        error: "Test Case ID is required",
      };
    }

    const params = {
      ...(page && { page }),
      ...(pageSize && { pageSize }),
    };

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/${projectId}/testcase/fetchExecutionHistory/${testCaseId}`,
      {
        params,
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error fetching execution history:", error);
    return extractErrorInfo(error);
  }
}

export async function bulkCreateTestCase({ testCases } = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!testCases || !Array.isArray(testCases) || testCases.length === 0) {
      return {
        status: STATUS.ERROR,
        error: "At least one test case is required",
      };
    }

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/${projectId}/testcase/bulkcreate`,
      { testCases },
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error bulk creating test cases:", error);
    return extractErrorInfo(error);
  }
}

export async function bulkSaveTestCase({ testCases } = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!testCases || !Array.isArray(testCases) || testCases.length === 0) {
      return {
        status: STATUS.ERROR,
        error: "At least one test case is required",
      };
    }

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/${projectId}/testcase/bulkSave`,
      testCases,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error bulk saving test cases:", error);
    return extractErrorInfo(error);
  }
}

export async function createTestCase(testCaseData = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!testCaseData.title || !testCaseData.type || !testCaseData.module_id) {
      return {
        status: STATUS.ERROR,
        error: "Title, type, and module_id are required",
      };
    }

    if (testCaseData.description !== undefined) {
      testCaseData.description = sanitizeForWaf(testCaseData.description);
    }

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/${projectId}/testcase/create`,
      testCaseData,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error creating test case:", error);
    return extractErrorInfo(error);
  }
}

